import { JSX, useEffect, type ReactNode } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { useAuth } from "react-oidc-context";
import { signOut } from "src/lib/user.helper";

import axiosPublic, { setDefaultAuthorizationToken } from "./axios";

function AxiosInterceptor({ children }: { children: ReactNode }): JSX.Element {
  const queryClient = useQueryClient();
  const auth = useAuth();

  // Set default header synchronously in render so children's first requests already carry the token.
  // Parent renders before children, so this runs before any React Query effect fires.
  if (auth.user?.access_token) {
    setDefaultAuthorizationToken(auth.user.access_token);
  }

  useEffect(() => {
    const requestInterceptor = axiosPublic.interceptors.request.use(
      async (config) => {
        if (auth.user?.access_token) {
          const updatedConfig = { ...config };
          updatedConfig.headers.authorization = `Bearer ${auth.user.access_token}`;
          return updatedConfig;
        }
        return config;
      },
      (error: AxiosError) => Promise.reject(error)
    );

    const responseInterceptor = axiosPublic.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          try {
            const newlyAuthenticatedUser = await auth.signinSilent();
            if (newlyAuthenticatedUser?.access_token) {
              originalRequest.headers.Authorization = `Bearer ${newlyAuthenticatedUser.access_token}`;
              setDefaultAuthorizationToken(newlyAuthenticatedUser.access_token);
              return axiosPublic(originalRequest); // Retry request
            }
            throw new Error("User not authenticated");
          } catch {
            signOut(queryClient, auth);
          }
        }
        throw error;
      }
    );

    return () => {
      axiosPublic.interceptors.request.eject(requestInterceptor);
      axiosPublic.interceptors.response.eject(responseInterceptor);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isAuthenticated, auth.user?.access_token]);

  // Don't mount children until OIDC has resolved the user from storage.
  // This prevents React Query from firing requests before the token is available.
  if (auth.isLoading) {
    return <></>;
  }

  return <>{children}</>;
}

export default AxiosInterceptor;
